import type { AppNotification } from "@/types/domain";

type NotificationRecord = Record<string, unknown>;

export type ListingReviewStatus = "pending" | "approved" | "rejected";

/** The canonical full-detail route used when an administrator reviews a listing. */
export function listingReviewDestination(listingId: string | number): string {
  return `/listing/${encodeURIComponent(String(listingId))}?review=1`;
}

export function listingReviewStatus(listing: {
  approved?: unknown;
  status?: unknown;
}): ListingReviewStatus {
  const status = String(listing.status || "").trim().toLowerCase();
  if (status === "rejected") return "rejected";

  const approved = String(listing.approved || "").trim().toLowerCase();
  if (
    status === "approved" ||
    ["1", "yes", "true", "approved"].includes(approved)
  )
    return "approved";

  return "pending";
}

function asRecord(value: unknown): NotificationRecord | undefined {
  return value && typeof value === "object"
    ? (value as NotificationRecord)
    : undefined;
}

function stringValue(
  item: AppNotification,
  ...keys: string[]
): string | undefined {
  const records = [item as NotificationRecord, asRecord(item.data)].filter(
    (record): record is NotificationRecord => Boolean(record),
  );

  for (const record of records) {
    for (const key of keys) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) return value.trim();
      if (typeof value === "number" && Number.isFinite(value))
        return String(value);
    }
  }

  return undefined;
}

function currentOrigin(): string | undefined {
  return typeof window === "undefined" ? undefined : window.location.origin;
}

/** Convert native/mobile notification links into routes understood by the portal. */
export function normalizeNotificationActionUrl(
  actionUrl?: string,
  origin = currentOrigin(),
): string | undefined {
  if (!actionUrl) return undefined;
  let normalized = actionUrl.trim();
  if (!normalized) return undefined;

  if (normalized.startsWith("carrfields://")) {
    normalized = `/${normalized.slice("carrfields://".length)}`;
  } else if (normalized.startsWith("exp://")) {
    const appPath = normalized.match(/\/--\/(.*)$/)?.[1];
    normalized = appPath ? `/${appPath}` : "/notifications";
  } else if (/^https?:\/\//i.test(normalized)) {
    if (!origin) return undefined;
    try {
      const url = new URL(normalized);
      if (url.origin !== origin) return undefined;
      normalized = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return undefined;
    }
  }

  normalized = normalized.replace(/^\/+/, "/");
  if (!normalized.startsWith("/")) normalized = `/${normalized}`;

  const match = normalized.match(/^([^?#]*)(.*)$/);
  let path = match?.[1] || "/";
  const suffix = match?.[2] || "";

  path = path
    .replace(/^\/\(tabs\)(?=\/|$)/, "")
    .replace(/^\/home$/, "/")
    .replace(/^\/admin\/addlisting$/, "/listings/new")
    .replace(/^\/admin\/mylistings$/, "/my-listings")
    .replace(/^\/admin\/adminDashboard$/, "/admin")
    .replace(/^\/admin\/notificationCenter$/, "/notifications")
    .replace(/^\/notificationCenter$/, "/notifications")
    .replace(/^\/admin\/manageUsers$/, "/admin/users")
    .replace(/^\/admin\/assignAgent$/, "/admin/assign-agent")
    .replace(/^\/admin\/bulk-upload-farmers$/, "/admin/bulk-upload")
    .replace(/^\/admin\/farmerAgentReport$/, "/admin/reports")
    .replace(/^\/admin\/soldListings$/, "/admin/sold-listings")
    .replace(
      /^\/admin\/auction-management\/create-auction$/,
      "/admin/auctions/create",
    )
    .replace(
      /^\/admin\/auction-management\/notifications$/,
      "/admin/auctions/notifications",
    )
    .replace(/^\/admin\/auction-management$/, "/admin/auctions")
    .replace(/^\/admin\/send-invitation$/, "/admin/invitations")
    .replace(/^\/agent\/groups$/, "/groups")
    .replace(/^\/previewListing$/, "/listings/preview")
    .replace(/^\/my-groups\/[^/]+$/, "/my-groups")
    .replace(
      /^\/admin\/auctions\/(?!create$|notifications$)([^/]+)$/,
      "/auction/$1",
    );

  const legacyListingReview = path.match(
    /^\/admin\/(?:review-listing\/([^/]+)|listings\/([^/]+)(?:\/review)?)$/,
  );
  if (legacyListingReview) {
    const listingId = legacyListingReview[1] || legacyListingReview[2];
    const url = new URL(`/listing/${listingId}${suffix}`, "https://portal.local");
    url.searchParams.set("review", "1");
    return `${url.pathname}${url.search}${url.hash}`;
  }

  return `${path || "/"}${suffix}`;
}

function assignAgentNotificationUrl(
  item: AppNotification,
): string | undefined {
  const farmerId = stringValue(item, "farmerId", "farmer_id");
  if (!farmerId) return undefined;

  const params = new URLSearchParams({ farmerId });
  const optionalParams = [
    ["farmerName", stringValue(item, "farmerName", "farmer_name", "name")],
    [
      "farmerFirstName",
      stringValue(item, "farmerFirstName", "farmer_first_name", "firstName"),
    ],
    [
      "farmerLastName",
      stringValue(item, "farmerLastName", "farmer_last_name", "lastName"),
    ],
    [
      "farmerRegion",
      stringValue(item, "farmerRegion", "farmer_region", "region"),
    ],
    ["notificationId", stringValue(item, "notificationId", "notification_id")],
  ] as const;

  for (const [key, value] of optionalParams) {
    if (value) params.set(key, value);
  }

  return `/admin/assign-agent?${params.toString()}`;
}

/** Resolve every supported notification shape to its portal destination. */
export function notificationDestination(
  item: AppNotification,
  userType?: string,
): string | undefined {
  const type = (
    stringValue(item, "type", "notificationType", "notification_type") || ""
  ).toLowerCase();
  const subType = (
    stringValue(item, "subType", "subtype", "sub_type") || ""
  ).toLowerCase();
  const listingId = stringValue(item, "listingId", "listing_id");
  const auctionId = stringValue(item, "auctionId", "auction_id");
  const groupId = stringValue(item, "groupId", "group_id");
  const recipientType = (
    stringValue(item, "recipientType", "recipient_type") || ""
  ).toLowerCase();
  const actionUrl = normalizeNotificationActionUrl(
    stringValue(item, "actionUrl", "action_url", "url"),
  );
  const screenUrl = normalizeNotificationActionUrl(stringValue(item, "screen"));

  if (
    ["listing_expiry", "listing_expiry_admin", "listing_rejected"].includes(
      type,
    ) &&
    listingId
  )
    return `/edit-listing/${encodeURIComponent(listingId)}`;

  if (type === "listing_expired") {
    if (recipientType === "watchlist_user") return actionUrl;
    if (listingId) return `/edit-listing/${encodeURIComponent(listingId)}`;
    return actionUrl;
  }

  const isListingApproval =
    type === "listing_approval" ||
    (type === "listing" && subType === "listing_approval");
  if (isListingApproval && listingId && userType === "admin")
    return listingReviewDestination(listingId);

  if (auctionId) return `/auction/${encodeURIComponent(auctionId)}`;

  if (type === "added_to_group" && !listingId) return "/my-groups";

  if (listingId) return `/listing/${encodeURIComponent(listingId)}`;

  if (
    groupId &&
    !["group_deleted", "removed_from_group"].includes(type)
  )
    return "/my-groups";

  if (type === "signup" && userType === "admin")
    return assignAgentNotificationUrl(item) || actionUrl || "/admin/users";

  if (type === "assignment" && !listingId && userType === "admin")
    return assignAgentNotificationUrl(item) || actionUrl || "/admin/users";

  return actionUrl || screenUrl;
}
